// ui/presenters/authorizationWindowLength.js
import { i18n } from 'boot/i18n';

import { daysBetween } from '../../domain/services/effectiveDating';

/**
 * Presenter: how long an authorization window runs, as the one figure a biller checks against the
 * paper. It converts nothing — the hour equivalents a stated figure might allow are consumption's.
 *
 * ONE UNIT, CHOSEN BY LENGTH, never a unit plus a remainder. A letter states a window in months, or
 * as two dates; it never says "four weeks and three days", so the remainder has nothing to be
 * checked against while making the one derived figure on the screen harder to read. The errors this
 * catches — a wrong year, a transposed month, a start read off the wrong row — are wrong by weeks or
 * months, and a remainder never changes that verdict.
 *
 * WEEKS ALONE FAIL AT BOTH ENDS, which is why the unit moves. Below a fortnight they round a
 * three-day window to zero, and a window whose dates are the same day is exactly the slip this is
 * here to catch. Past a few months they stop being checkable: nobody can tell whether 72 weeks
 * matches a letter reading "through Dec 15, 2027", where 16 months answers at a glance.
 */

const DAYS_PER_WEEK = 7;

/**
 * The average Gregorian month, so a span in days can be named in months. The figure is a check
 * against paper and not arithmetic anyone bills on, and it agrees with the calendar on the windows
 * funders actually write: Jan 1–Jun 30 reads 6 months, a full year 12.
 */
const DAYS_PER_MONTH = 30.44;

/** Below a fortnight, days. From there to a quarter, weeks. Beyond it, months. */
const SHORTEST_IN_WEEKS = 14;
const SHORTEST_IN_MONTHS = 90;

/**
 * Formats the inclusive authorization window as a localized length: days below 14 days, rounded
 * weeks from 14 through 89 days, and rounded months (30.44 days each) from 90 days onward.
 * Date objects are unreadable; longer strings use their first ten characters without timezone
 * conversion.
 *
 * @param {string|null|undefined} periodStart - start date in 'YYYY-MM-DD' form
 * @param {string|null|undefined} periodEnd - end date in 'YYYY-MM-DD' form
 * @returns {string|null} localized length, or null if either bound is missing or unreadable,
 *   or the end precedes the start
 */
// eslint-disable-next-line import/prefer-default-export
export function authorizationWindowLength(periodStart, periodEnd) {
  const span = daysBetween(periodStart, periodEnd);

  if (span === null || span < 0) {
    return null;
  }

  // The window is INCLUSIVE of both bounds, so a span of 6 days covers 7; `daysBetween` measures
  // the difference and leaves the +1 to whoever knows that.
  const days = span + 1;

  // `tc`, not `t`. vue-i18n is in legacy mode here, where `t` picks the plural form from
  // `named.count` and ignores the positional one — so these keys, whose parameter is named for the
  // unit, rendered "2 week". `tc` is what the rest of the codebase uses and reads the count it is given.
  if (days < SHORTEST_IN_WEEKS) {
    return i18n.tc('billingAuthorizations.windowDays', days, { days });
  }

  if (days < SHORTEST_IN_MONTHS) {
    const weeks = Math.round(days / DAYS_PER_WEEK);

    return i18n.tc('billingAuthorizations.windowWeeks', weeks, { weeks });
  }

  const months = Math.round(days / DAYS_PER_MONTH);

  return i18n.tc('billingAuthorizations.windowMonths', months, { months });
} // authorizationWindowLength()
